// The Ask "propose" step -- `POST /api/ask/preview` in the design's own
// vocabulary (`#foundation` "Ask flow" gallery step 2, `#s07`).
//
// Per the 2026-09-06 UX execution plan §9 conflict #9: "Ask preview/commit...
// none has an M1 endpoint... hooks return `unavailable`, pages render the
// honest state. Fixtures drive tests and the dev harness only." There is
// nothing to fetch, so this hook does NOT attempt a network call in normal
// operation -- it resolves synchronously to an error every caller can map to
// the same "Ask lands in M2" honest state (WS-5 step 7), rather than
// guaranteeing a 404 against a real backend on every keystroke.
//
// The zod schemas below exist so the type this hook WOULD return is pinned
// now (the LLM proposes labels, never ids -- the server resolves them, per
// the binding rule), and so `ask.fixtures.ts` has a live contract to be
// checked against for the dev harness and colocated tests.
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { confidenceLevelSchema } from './useInboxItems';

export const askMatchRowSchema = z.object({
  id: z.string(),
  merchant: z.string(),
  account_last4: z.string().nullable(),
  posted_at: z.string(),
  pending: z.boolean(),
  amount: z.string(),
  currency: z.string(),
  confidence: confidenceLevelSchema,
  struck: z.boolean(), // bank-amended/removed since match, per the design's "Adobe" row
});
export type AskMatchRow = z.infer<typeof askMatchRowSchema>;

export const askProposalSchema = z.object({
  draft_id: z.string(),
  sentence: z.string(),
  window: z.object({ start: z.string(), end: z.string(), clustered: z.boolean() }),
  account_last4s: z.array(z.string()),
  event_name: z.string(),
  categories: z.array(z.string()),
  location: z.string().nullable(),
  matches: z.array(askMatchRowSchema),
  likely_related: z.array(askMatchRowSchema),
  degraded_sources: z.array(z.object({ institution_name: z.string(), reason: z.string() })),
  expires_at: z.string(),
  // Present when a sync landed matches since a PRIOR preview of the same
  // draft (i.e. this IS a re-preview response) -- drives the
  // `preview-superseded` banner (`#s07` tablet). Absent/0 on a first preview.
  new_matches_since_last_preview: z.number().nullable().optional(),
});
export type AskProposal = z.infer<typeof askProposalSchema>;

/** Thrown by every Ask mutation in place of a network call -- see file header. */
export class AskUnavailableError extends Error {
  constructor() {
    super('Ask preview/commit has no M1 endpoint. No demonstration answer has been substituted.');
    this.name = 'AskUnavailableError';
  }
}

export function useAskPreview() {
  return useMutation<AskProposal, AskUnavailableError, { sentence: string }>({
    mutationFn: async () => {
      throw new AskUnavailableError();
    },
  });
}
